export const normalizeSelectedPanelIndices = (raw = []) => {
  if (!Array.isArray(raw)) return [];
  const unique = Array.from(
    new Set(
      raw
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
    )
  );
  unique.sort((a, b) => a - b);
  return unique;
};

export const normalizeSelectedPanelsBySegment = (source = {}) => {
  if (!source || typeof source !== 'object') return {};
  const normalized = {};
  Object.keys(source).forEach((segmentKey) => {
    const segmentIndex = Number(segmentKey);
    if (!Number.isFinite(segmentIndex)) return;
    const selected = normalizeSelectedPanelIndices(source[segmentKey]);
    if (selected.length > 0) normalized[segmentIndex] = selected;
  });
  return normalized;
};

export const getSelectedPanelsBySegmentFromPanels = (panels = []) => {
  const mapped = {};
  if (!Array.isArray(panels)) return mapped;

  panels.forEach((panel, segmentIndex) => {
    const horizontal = Array.isArray(panel?.horizontal?.selected)
      ? panel.horizontal.selected
      : [];
    const vertical = Array.isArray(panel?.vertical?.selected)
      ? panel.vertical.selected
      : [];
    const selected = [];

    horizontal.forEach((isSelected, panelIndex) => {
      if (isSelected) selected.push(panelIndex);
    });

    const horizontalCount = horizontal.length;
    vertical.forEach((isSelected, panelIndex) => {
      if (isSelected) selected.push(panelIndex + horizontalCount);
    });

    if (selected.length > 0) mapped[segmentIndex] = selected;
  });

  return mapped;
};

export const buildProductionPanelSelection = (
  panels,
  selectedPanelsByRoof = {}
) => {
  const selectedObjectsPanels = {};
  let hasSelectedPanels = false;

  const nextPanels = Array.isArray(panels)
    ? panels.map((panel, segmentIndex) => {
        const horizontalPoints = Array.isArray(panel?.horizontal?.points)
          ? panel.horizontal.points
          : [];
        const verticalPoints = Array.isArray(panel?.vertical?.points)
          ? panel.vertical.points
          : [];
        const rawSelected =
          selectedPanelsByRoof?.[segmentIndex] ??
          selectedPanelsByRoof?.[String(segmentIndex)] ??
          [];
        const selectedSet = new Set();

        if (Array.isArray(rawSelected)) {
          rawSelected.forEach((rawIndex) => {
            const panelIndex = Number(rawIndex);
            if (
              Number.isInteger(panelIndex) &&
              panelIndex >= 0 &&
              panelIndex < horizontalPoints.length + verticalPoints.length
            ) {
              selectedSet.add(panelIndex);
            }
          });
        }

        const selectedList = Array.from(selectedSet).sort((a, b) => a - b);
        if (selectedList.length > 0) {
          selectedObjectsPanels[segmentIndex] = selectedList;
          hasSelectedPanels = true;
        }

        return {
          ...panel,
          horizontal: {
            ...panel?.horizontal,
            selected: horizontalPoints.map((_, panelIndex) =>
              selectedSet.has(panelIndex)
            )
          },
          vertical: {
            ...panel?.vertical,
            selected: verticalPoints.map((_, panelIndex) =>
              selectedSet.has(panelIndex + horizontalPoints.length)
            )
          }
        };
      })
    : panels;

  return {
    hasSelectedPanels,
    panels: nextPanels,
    selectedObjectsPanels
  };
};

export const countSelectedPanelFlags = (selectedPanels = {}) => {
  if (!selectedPanels || typeof selectedPanels !== 'object') return 0;
  return Object.values(selectedPanels).reduce(
    (total, roofPanels) =>
      total +
      (Array.isArray(roofPanels)
        ? roofPanels.reduce((sum, value) => sum + (value ? 1 : 0), 0)
        : 0),
    0
  );
};

export const countSelectedPanelIndices = (selectedPanels = {}) => {
  if (!selectedPanels || typeof selectedPanels !== 'object') return 0;
  return Object.values(selectedPanels).reduce(
    (total, roofPanels) =>
      total + (Array.isArray(roofPanels) ? roofPanels.length : 0),
    0
  );
};

export const getSelectedPanelBreakdown = (selectedPanels = {}) => {
  if (!selectedPanels || typeof selectedPanels !== 'object') return {};
  return Object.entries(selectedPanels).reduce(
    (result, [roofId, roofPanels]) => {
      result[roofId] = Array.isArray(roofPanels)
        ? roofPanels.reduce((sum, value) => sum + (value ? 1 : 0), 0)
        : 0;
      return result;
    },
    {}
  );
};
